// /web/src/components/Layout.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../lib/api";

// Cookie consent
import { ConsentProvider, useConsent } from "../consent/ConsentContext";
import CookieBanner from "../consent/CookieBanner";
import CookieSettingsModal from "../consent/CookieSettingsModal";

import {
  identifyAmplitudeUser,
  resetAmplitudeIdentity,
  setAmplitudeConsent,
  trackAmplitudeEvent,
  trackPageView,
} from "../lib/amplitude";

import SiteHeader from "./SiteHeader.jsx";

const NAV_LINKS = [
  { to: "/benefits", label: "Benefits" },
  { to: "/how-to", label: "How-to" },
  { to: "/blog", label: "Blog" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact us" },
];




function Shell() {
  const { isAuthenticated, isLoading: authLoading, logout, loginWithRedirect } = useAuth0();
  const { prefs } = useConsent();
  const consent = !!prefs?.analytics;
  const api = useApi();
  const location = useLocation();

  // Header “plan/tokens” summary
  const [plan, setPlan] = useState(null);
  const [tokens, setTokens] = useState(null);

  const lastTrackedPath = useRef(null);

  // Fetch plan + tokens (same endpoints you use on Dashboard)
  const loadHeaderMeta = useCallback(async () => {
    if (!isAuthenticated) {
      setPlan(null);
      setTokens(null);
      // clear any stale id if logged out
      try { window.localStorage.removeItem("auth0_sub"); } catch {}
      resetAmplitudeIdentity();
      return;
    }
    try {
      const [me, meTokens] = await Promise.all([api("/me"), api("/me/tokens")]);
      // Save minimal identity for analytics
      if (me?.sub) {
        try { window.localStorage.setItem("auth0_sub", me.sub); } catch {}
      }
      setPlan(me?.plan ?? null);
      setTokens(
        typeof meTokens?.tokens_remaining === "number" ? meTokens.tokens_remaining : null
      );
    } catch {
      /* ignore */
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    loadHeaderMeta();
  }, [authLoading, loadHeaderMeta]);

  // Update tokens live when calculators report usage
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTokenUpdate = (event) => {
      const remaining = event?.detail?.remaining;
      if (typeof remaining === "number") setTokens(remaining);
    };
    window.addEventListener("pd_tokens_update", onTokenUpdate);
    return () => window.removeEventListener("pd_tokens_update", onTokenUpdate);
  }, []);

  // --- Analytics bootstrap (GDPR-aware) ---
  // 1) Initialize analytics only after consent
  useEffect(() => {
    setAmplitudeConsent(!!consent);
    if (!consent) {
      lastTrackedPath.current = null;
    }
  }, [consent]);

  // 2) Identify (after we know auth + plan/tokens)
  useEffect(() => {
    if (!isAuthenticated || !consent) return;
    const id = (typeof window !== "undefined" && window.localStorage.getItem("auth0_sub")) || "";
    if (!id) return; // avoid anonymous identify
    identifyAmplitudeUser(id, {
      plan: plan || undefined,
      tokens_remaining: typeof tokens === "number" ? tokens : undefined,
    });
  }, [isAuthenticated, consent, plan, tokens]);

  // Track page views on SPA navigation (after consent granted)
  useEffect(() => {
    if (!consent) return;
    const path = `${location.pathname}${location.search}`;
    if (lastTrackedPath.current === path) return;
    lastTrackedPath.current = path;
    trackPageView({
      path: location.pathname,
      search: location.search,
      title: typeof document !== "undefined" ? document.title : undefined,
    });
  }, [consent, location.pathname, location.search]);

  // Auth0 helpers
  const handleLogout = useCallback(
    () => logout({ logoutParams: { returnTo: window.location.origin } }),
    [logout]
  );

  const handleLogin = useCallback(() => loginWithRedirect(), [loginWithRedirect]);
  const handleSignup = useCallback(() => {
    trackAmplitudeEvent("upgrade_clicked", { context: "header" });
    loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
  }, [loginWithRedirect]);

  const hidePlanSummaryRoutes = ["/blog", "/pricing", "/contact", "/contact-us", "/terms", "/privacy"];
  const showPlanSummary = !hidePlanSummaryRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  return (
    <div className="app-shell pd-root">
      <SiteHeader
        navLinks={NAV_LINKS}
        isAuthenticated={isAuthenticated}
        isLoading={authLoading}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
        plan={plan}
        showPlanSummary={showPlanSummary}
      />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function Footer() {
  const { showSettings } = useConsent();

  return (
    <footer className="pd-footer" id="contact">
      <div className="pd-container pd-footer__inner">
        <p className="pd-muted">© Atlas of Change {new Date().getFullYear()}</p>
        <nav className="pd-footer__nav" aria-label="Footer">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Use</Link>
          <Link to="/contact">Contact us</Link>
          <Link to="/cookies">Cookie Policy</Link>
          <button
            type="button"
            className="pd-footer__nav-button"
            onClick={showSettings}
          >
            Cookie settings
          </button>
        </nav>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <ConsentProvider>
      <Shell />
      {/* Cookie consent UI */}
      <CookieBanner />
      <CookieSettingsModal />
    </ConsentProvider>
  );
}
